/**
 * Mandate — Transition Governance System
 * TypeScript types matching the 480_mandate_tables.sql schema.
 */

// =============================================================================
// Enums / Union Types
// =============================================================================

export type STaRSSituation = 'startup' | 'turnaround' | 'realignment' | 'sustaining';

export type MandateTransitionStatus = 'setup' | 'active' | 'complete' | 'archived';

export type MandatePriorityStatus = 'active' | 'completed' | 'dropped';

export type StakeholderInfluence = 'high' | 'medium' | 'low';

export type StakeholderStance = 'ally' | 'fence-sitter' | 'skeptic' | 'opponent';

// =============================================================================
// Core Domain Objects
// =============================================================================

export interface MandateTransition {
  id: string;
  workspaceId: string;
  ownerId: string;
  /** Dedicated personal collaboration space (Individual Space). Migration 490. */
  spaceId: string | null;
  /** Personal prioritization_context (P01). Migration 490. */
  contextId: string | null;
  title: string;
  organization: string;
  role: string;
  startDate: string | null;
  endDate: string | null;
  situationType: STaRSSituation | null;
  situationNotes: string | null;
  status: MandateTransitionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MandateOutcomeVersion {
  id: string;
  transitionId: string;
  hypothesis: string;
  version: number;
  revisionReason: string | null;
  revisedFromId: string | null;
  isCurrent: boolean;
  createdAt: string;
}

export interface MandatePriority {
  id: string;
  transitionId: string;
  title: string;
  description: string | null;
  rank: number;
  rationale: string | null;
  status: MandatePriorityStatus;
  /** Linked workspace item when promoted (origin_module = mandate). */
  platformItemId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MandatePriorityRerank {
  id: string;
  transitionId: string;
  priorityId: string;
  previousRank: number | null;
  newRank: number | null;
  signal: string;
  occurredAt: string;
}

export interface MandateDecision {
  id: string;
  transitionId: string;
  title: string;
  description: string | null;
  rationale: string;
  signal: string | null;
  peopleConsulted: string | null;
  outcome: string | null;
  /** Linked platform decision when mirrored. */
  platformDecisionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MandateStakeholder {
  id: string;
  transitionId: string;
  name: string;
  role: string | null;
  influence: StakeholderInfluence | null;
  stance: StakeholderStance | null;
  trustGaps: string | null;
  notes: string | null;
  /** Linked people-type item when promoted. */
  platformItemId: string | null;
  createdAt: string;
}

// =============================================================================
// Row → Domain Mappers
// =============================================================================

export function transitionRowToDomain(row: Record<string, unknown>): MandateTransition {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    ownerId: row.owner_id as string,
    spaceId: (row.space_id as string) ?? null,
    contextId: (row.context_id as string) ?? null,
    title: (row.title as string) ?? '',
    organization: (row.organization as string) ?? '',
    role: (row.role as string) ?? '',
    startDate: (row.start_date as string) ?? null,
    endDate: (row.end_date as string) ?? null,
    situationType: (row.situation_type as STaRSSituation) ?? null,
    situationNotes: (row.situation_notes as string) ?? null,
    status: (row.status as MandateTransitionStatus) ?? 'setup',
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function outcomeVersionRowToDomain(row: Record<string, unknown>): MandateOutcomeVersion {
  return {
    id: row.id as string,
    transitionId: row.transition_id as string,
    hypothesis: row.hypothesis as string,
    version: row.version as number,
    revisionReason: (row.revision_reason as string) ?? null,
    revisedFromId: (row.revised_from_id as string) ?? null,
    isCurrent: row.is_current as boolean,
    createdAt: row.created_at as string,
  };
}

export function priorityRowToDomain(row: Record<string, unknown>): MandatePriority {
  return {
    id: row.id as string,
    transitionId: row.transition_id as string,
    title: row.title as string,
    description: (row.description as string) ?? null,
    rank: row.rank as number,
    rationale: (row.rationale as string) ?? null,
    status: (row.status as MandatePriorityStatus) ?? 'active',
    platformItemId: (row.platform_item_id as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function rerankRowToDomain(row: Record<string, unknown>): MandatePriorityRerank {
  return {
    id: row.id as string,
    transitionId: row.transition_id as string,
    priorityId: row.priority_id as string,
    previousRank: (row.previous_rank as number) ?? null,
    newRank: (row.new_rank as number) ?? null,
    signal: row.signal as string,
    occurredAt: row.occurred_at as string,
  };
}

export function decisionRowToDomain(row: Record<string, unknown>): MandateDecision {
  return {
    id: row.id as string,
    transitionId: row.transition_id as string,
    title: row.title as string,
    description: (row.description as string) ?? null,
    rationale: row.rationale as string,
    signal: (row.signal as string) ?? null,
    peopleConsulted: (row.people_consulted as string) ?? null,
    outcome: (row.outcome as string) ?? null,
    platformDecisionId: (row.platform_decision_id as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function stakeholderRowToDomain(row: Record<string, unknown>): MandateStakeholder {
  return {
    id: row.id as string,
    transitionId: row.transition_id as string,
    name: row.name as string,
    role: (row.role as string) ?? null,
    influence: (row.influence as StakeholderInfluence) ?? null,
    stance: (row.stance as StakeholderStance) ?? null,
    trustGaps: (row.trust_gaps as string) ?? null,
    notes: (row.notes as string) ?? null,
    platformItemId: (row.platform_item_id as string) ?? null,
    createdAt: row.created_at as string,
  };
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Returns the 1-indexed day number since the transition start date.
 * Day 1 = the start date itself. Counted in calendar days where the person
 * is: a date-only start ("2026-10-01") is that local day, not UTC midnight,
 * which put the evening a day ahead in the Americas. First 100 Days counts its
 * journey with this too (roadmap S-16).
 */
export function getDayNumber(startDate: string, now: Date = new Date()): number {
  const [y, m, d] = startDate.slice(0, 10).split('-').map(Number);
  const start = new Date(y, (m || 1) - 1, d || 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(1, days + 1);
}

/**
 * Returns the number of days remaining until the end date.
 * Returns 0 if the end date has passed.
 */
export function getDaysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.ceil((end.getTime() - now.getTime()) / msPerDay);
  return Math.max(0, diff);
}
